import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  isAdminPreviewSession,
  isItemInPreviewSet,
  mergePreviewIntoStores,
  parsePreviewQuery,
  PREVIEW_OVERLAY_EVENT,
  previewItemKey,
  readPreviewOverlay,
} from "./cmsPreview";

export const CmsPreviewContext = createContext(null);

export function CmsPreviewProvider({ children }) {
  const reduxContent = useSelector((s) => s.contentData.content);
  const reduxCatalog = useSelector((s) => s.catalogData.catalog);
  const user = useSelector((s) => s.auth.user);
  const location = useLocation();
  const search = location.search || "";
  const [overlayTick, setOverlayTick] = useState(0);

  useEffect(() => {
    const bump = () => setOverlayTick((n) => n + 1);
    window.addEventListener(PREVIEW_OVERLAY_EVENT, bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener(PREVIEW_OVERLAY_EVENT, bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  const value = useMemo(() => {
    const overlay = readPreviewOverlay();
    const query = parsePreviewQuery(search);
    const merged = mergePreviewIntoStores({
      content: reduxContent,
      catalog: reduxCatalog,
      overlay,
      query,
      user,
      search,
    });
    const isPreview = isAdminPreviewSession({ user, search });
    return {
      content: merged.content,
      catalog: merged.catalog,
      isPreview,
      overlay,
      previewItems: overlay?.items || [],
      query,
      user,
    };
  }, [reduxContent, reduxCatalog, user, search, overlayTick]);

  return (
    <CmsPreviewContext.Provider value={value}>
      {children}
    </CmsPreviewContext.Provider>
  );
}

export function usePreviewStores() {
  const ctx = useContext(CmsPreviewContext);
  const reduxContent = useSelector((s) => s.contentData.content);
  const reduxCatalog = useSelector((s) => s.catalogData.catalog);
  if (ctx) return ctx;
  return {
    content: reduxContent,
    catalog: reduxCatalog,
    isPreview: false,
    overlay: null,
    previewItems: [],
    query: parsePreviewQuery(""),
    user: null,
  };
}

export function useIsInPreviewSet(section, identifier) {
  const ctx = useContext(CmsPreviewContext);
  const items = ctx?.previewItems;
  if (!identifier) return false;
  const key = previewItemKey({ section, identifier });
  if (Array.isArray(items)) {
    return items.some((item) => previewItemKey(item) === key);
  }
  return isItemInPreviewSet({ section, identifier });
}
